import React, { useState, useCallback, ReactElement, useEffect, useRef } from 'react';
import theme from './FileUploadWizard.module.scss';
import { notEmptyPredicate } from '@ygtechnologies/rubus-frontend-utilities';
import { FileUpload, Modal, ModalBody, ModalHeader, ImageDraw, ImageDrawRef, LoadingOverlay, SimpleWizard, SimpleWizardStep, SimpleWizardConfirmStep } from '@ygtechnologies/bygr-frontend-component-library';
import { FileData, FileType } from '@ygtechnologies/rubus-frontend-utilities';
import { Button } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { useOnWindowResize } from '@/utils/hooks';
import { TranslatableText } from '@/types';
import FileUploadUnitDocuments from './FileUploadUnitDocuments/FileUploadUnitDocuments';
import { pdfToImage, getOpenDocumentUrl } from '@/services/documents';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { calculateSizeFit, Size } from '@/utils/graphics';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LargeRadio } from '@/components/forms/Radio';
import Banner from '@/components/Banner';

export interface FileUploadWizardProps {
    isOpen: boolean;
    onFileChange(files: FileData[]): void;
    toggle(): void;
    initialFileData?: FileData | null;
    imageEditContinueButtonText?: TranslatableText;
    residentialUnitId?: number;
    disableDocumentAccess?: boolean;
    skipSuccessStep?: boolean;
}
enum Step {
    Intro,
    EditImage,
    // PreviewImage,
    Success,
    UnitFileSelect,
}

export const FileUploadWizard: React.FC<FileUploadWizardProps> = ({
    isOpen,
    onFileChange,
    toggle,
    skipSuccessStep = false,
    initialFileData,
    imageEditContinueButtonText,
    residentialUnitId,
    disableDocumentAccess = false,
}) => {
    const [imageSize, setImageSize] = useState<Size | null>(null);
    const [originalFile, setOriginalFile] = useState<null | FileData>(null);
    const [file, setFile] = useState<null | FileData>(null);
    const [currentStep, setCurrentStep] = useState(Step.Intro);
    const [selectedVersionId, setSelectedVersionId] = useState<null | number>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadMode, setUploadMode] = useState<'filesystem' | 'document'>('filesystem');
    const [drawValue, setDrawValue] = useState<any>(null);
    const [hasChanges, setHasChanges] = useState(false);

    const [pdfFailed, setPdfFailed] = useState(false);

    const handleStepChange = (step: Step) => {
        if (step === Step.Intro || step === Step.UnitFileSelect) {
            setFile(null);
            setOriginalFile(null);
            setSelectedVersionId(null);
            setDrawValue(null);
        }

        if (disableDocumentAccess && step === Step.UnitFileSelect) {
            return;
        }

        setCurrentStep(step);
    };

    const canDraw = !originalFile || [FileType.Image, FileType.Pdf].includes(originalFile.getFileType());

    const imageDrawRef = useRef<ImageDrawRef>(null);

    const steps = [
        Step.Intro,
        uploadMode === 'document' ? Step.UnitFileSelect : null,
        canDraw ? Step.EditImage : null,
        !skipSuccessStep ? Step.Success : null,
    ].filter(notEmptyPredicate);

    const goToSuccess = async () => {
        if (steps.includes(Step.Success)) {
            setCurrentStep(Step.Success);
        } else {
            await handleSubmit();
            handleFinish();
        }
    };

    const updateModalStyle = useCallback(async () => {
        if (!file) {
            return;
        }

        const maxSize = {
            width: window.innerWidth * 0.9,
            height: window.innerHeight * 0.9 - 100,
        };
        const imageSize = await file.getImageSize();

        const scaledImageSize = calculateSizeFit(imageSize, maxSize);

        setImageSize(scaledImageSize);
    }, [file]);

    useEffect(() => {
        updateModalStyle();
    }, [updateModalStyle]);
    useOnWindowResize(updateModalStyle, 50);

    const onDrawChange = useCallback((value: any, hasChanges: boolean) => {
        setHasChanges(hasChanges);
        setDrawValue(value);
    }, []);

    const handleNewFile = useCallback(
        async (newFile: FileData) => {
            setIsLoading(true);
            const fileType = newFile.getFileType();

            if (fileType === FileType.Image) {
                setOriginalFile(newFile);
                setFile(newFile);
                setCurrentStep(Step.EditImage);
            } else if (fileType === FileType.Pdf) {
                setOriginalFile(newFile);
                // If the pdf convert to image fails for some reason,
                // we still give them the option to upload the document.
                try {
                    const convertedToImage = await pdfToImage(newFile);
                    setFile(convertedToImage);
                    setCurrentStep(Step.EditImage);
                } catch (e: any) {
                    triggerHandler(onFileChange, [newFile]);
                    setPdfFailed(true);
                    goToSuccess();
                }
            } else {
                setOriginalFile(newFile);
                triggerHandler(onFileChange, [newFile]);
                goToSuccess();
            }
            setIsLoading(false);
        },
        [onFileChange]
    );

    const handleFileDrop = useCallback(
        async (files: File[]) => {
            if (files.length > 0) {
                const droppedFileData = new FileData(files[0]);
                await handleNewFile(droppedFileData);
            }
        },
        [handleNewFile]
    );

    // const previousStep = usePrevious(currentStep);
    // useEffect(() => {
    //     if (previousStep === currentStep || currentStep !== Step.Intro) {
    //         return;
    //     }

    //     if (initialFileData) {
    //         handleNewFile(initialFileData);
    //     } else {
    //         setOriginalFile(null);
    //         setFile(null);
    //     }
    // }, [initialFileData, handleNewFile, previousStep, currentStep]);

    const createNewFileData = async () => {
        const imageDataUrl = imageDrawRef.current?.getImageDataUrl();

        if (imageDataUrl) {
            const fileData = await FileData.fromDataUrl(imageDataUrl, file!.fileName);
            triggerHandler(onFileChange, [fileData]);
        } else {
            triggerHandler(onFileChange, [originalFile!]);
        }

        goToSuccess();
    };

    const downloadFileData = async () => {
        setIsLoading(true);
        const fileData = await FileData.download(getOpenDocumentUrl(selectedVersionId!));

        if (fileData.getFileType() === FileType.Pdf) {
            fileData.originalFile = new File([fileData.originalFile], fileData.fileName);
        }

        await handleNewFile(fileData);

        return false;
    };

    const back = () => {
        const fileType = initialFileData?.getFileType();
        if (fileType === FileType.Image || fileType === FileType.Pdf) {
            setCurrentStep(Step.EditImage);
        } else {
            reset();
        }
    };

    const reset = () => {
        setImageSize(null);
        setFile(null);
        setOriginalFile(null);
        setCurrentStep(Step.Intro);
        setSelectedVersionId(null);
        setUploadMode('filesystem');
        setDrawValue(null);
    };

    const handleOpen = () => {
        reset();
        if (initialFileData) {
            // setOriginalFile(initialFileData);
            setCurrentStep(Step.EditImage);
            handleNewFile(initialFileData);
        }
    };

    const handleClose = () => {
        toggle();
    };

    const handleFinish = () => {
        toggle();
    };

    const handleSubmit = async () => {
        const imageDataUrl = imageDrawRef.current?.getImageDataUrl();

        if (hasChanges && imageDataUrl) {
            const fileData = await FileData.fromDataUrl(imageDataUrl, file!.fileName);

            triggerHandler(onFileChange, [fileData]);
        } else {
            triggerHandler(onFileChange, [originalFile!]);
        }
    };

    return (
        <Modal
            onOpen={handleOpen}
            className={theme.modal}
            // size="auto"
            size={currentStep === Step.EditImage ? 'auto' : 'lg'}
            isOpen={isOpen}
            toggle={handleClose}>
            <ModalHeader>
                <Trans>Upload an attachment</Trans>
            </ModalHeader>
            <ModalBody>
                <LoadingOverlay visible={isLoading}>
                    <SimpleWizard
                        onCancel={handleClose}
                        onSubmit={handleSubmit}
                        onFinish={handleFinish}
                        steps={steps}
                        currentStep={currentStep}
                        onStepChange={handleStepChange}
                        saveButtonProps={imageEditContinueButtonText ? { children: imageEditContinueButtonText } : undefined}
                        waitForSubmit={false}
                        inheritValidationScope={false}>
                        <SimpleWizardStep
                            stepNumber={Step.Intro}
                            header={<Trans>Choose where you would like to upload from</Trans>}
                            nextButton={
                                uploadMode === 'filesystem'
                                    ? (props) => (
                                          <FileUpload unstyled single onDrop={handleFileDrop}>
                                              <Button {...props} onClick={undefined}>
                                                  <Trans>Choose file</Trans>
                                              </Button>
                                          </FileUpload>
                                      )
                                    : undefined
                            }>
                            <div className="flex column">
                                <LargeRadio
                                    onChange={() => setUploadMode('filesystem')}
                                    checked={uploadMode === 'filesystem'}>
                                    <Trans>
                                        <strong>Upload from this computer</strong>
                                        Find and upload a file from your computer.
                                    </Trans>
                                </LargeRadio>
                                {residentialUnitId && (
                                    <LargeRadio
                                        disabled={disableDocumentAccess}
                                        onChange={() => setUploadMode('document')}
                                        checked={uploadMode === 'document'}>
                                        <Trans>
                                            <strong>Upload from unit documents</strong>
                                            Find and upload an existing unit document, to browse and select the document
                                            version you want, click on 'Show details'.
                                        </Trans>
                                    </LargeRadio>
                                )}
                            </div>
                        </SimpleWizardStep>

                        <SimpleWizardStep
                            onNext={downloadFileData}
                            stepNumber={Step.UnitFileSelect}
                            nextButton={(props) => <Button {...props} disabled={!selectedVersionId} />}
                            header={<Trans>Choose a unit document to upload</Trans>}>
                            <FileUploadUnitDocuments
                                selectedVersionId={selectedVersionId}
                                onSelect={(versionId) => setSelectedVersionId(versionId)}
                                residentialUnitId={residentialUnitId || 0}
                            />
                        </SimpleWizardStep>

                        <SimpleWizardStep
                            onNext={createNewFileData}
                            stepNumber={Step.EditImage}
                            header={
                                <>
                                    <Trans>Edit</Trans> {originalFile?.fileName}
                                </>
                            }>
                            <div className={theme.imageEditStepContent}>
                                <div className={theme.imageWrapper}>
                                    <div className={theme.imageContainer}>
                                        {file && imageSize && (
                                            <ImageDraw
                                                ref={imageDrawRef}
                                                onChange={onDrawChange}
                                                maxHeightInPx={imageSize.height}
                                                maxWidthInPx={imageSize.width - 30}
                                                backgroundImage={file}
                                                value={drawValue}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </SimpleWizardStep>

                        <SimpleWizardConfirmStep stepNumber={Step.Success} header={<Trans>Document uploaded</Trans>}>
                            {pdfFailed && (
                                <Banner type="warning">
                                    <Trans>
                                        <strong>Please note</strong> generating a preview of this document failed, therefore
                                        you will not be able edit this document before you upload it.
                                    </Trans>
                                </Banner>
                            )}
                            <div className={theme.success}>
                                <FontAwesomeIcon icon={faCheckCircle} size={'10x'} />
                                <h5>
                                    <Trans>Document uploaded successfully.</Trans>
                                </h5>
                            </div>
                        </SimpleWizardConfirmStep>
                    </SimpleWizard>

                    {/* {currentStep.render()} */}
                </LoadingOverlay>
            </ModalBody>
        </Modal>
    );
};
